import { useRef, useState } from "react"
import { useMeStore } from "@/features/auth/hooks/useCurrentUser"
import { UNCONFIRMED_COPY, buyAddon, startSubscription, type PlanChoice } from "../services/razorpayCheckout"

/** What to tell the reader after a checkout, or null when nothing needs saying (paid, or closed on purpose). */
export function checkoutMessage(result: string): string | null {
  if (result === "unconfirmed") return UNCONFIRMED_COPY
  if (result === "pending") return "Payment received. Activation is taking longer than usual; this page will update shortly."
  return null
}

/** Checkout for a plan or an add-on, one at a time, with the busy flag and the error line a button needs. */
export function useCheckout() {
  const me = useMeStore((s) => s.me)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState("")
  const inFlight = useRef(false)
  const prefill = { name: me?.display_name, email: me?.email }

  const run = async (key: string, fn: () => Promise<string>): Promise<string> => {
    if (inFlight.current) return "busy"
    inFlight.current = true
    setBusy(key)
    setError("")
    try {
      const result = await fn()
      const msg = checkoutMessage(result)
      if (msg) setError(msg)
      return result
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start checkout")
      return "error"
    } finally {
      inFlight.current = false
      setBusy(null)
    }
  }

  return {
    busy, error, setError,
    subscribe: (plan: PlanChoice) => run(plan, () => startSubscription(plan, prefill, setError)),
    buy: (sku: string, scopeKey?: string | null) => run(sku, () => buyAddon(sku, scopeKey, prefill, setError)),
  }
}
